import {
  Button,
  Paper,
  Stack,
  Text,
  Title,
  Box,
  Flex,
  Timeline,
  Group,
  Badge,
  Loader,
} from "@mantine/core";
import {
  IconClock,
  IconFilter,
  IconMapPin,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useGetPublicSessions } from "../../api/session";
import { getI18nText } from "../../utils/i18nUtils";
import { getTranslatedDay, getTranslatedMonth } from "@congrs/utils";

const isOpenable = (session) =>
  session.isClickable &&
  !session.canceledAt &&
  Boolean(
    session.description?.trim() ||
      session.sessionLinks?.length ||
      session.speakers?.length ||
      session.sponsor?.id
  );

const Agenda = ({
  handleSessionClick,
  sessionParams,
  hasActiveFilters,
  clearFilters,
}) => {
  const { data: sessions, isPending } = useGetPublicSessions(sessionParams);
  const { t, i18n } = useTranslation();

  if (isPending) {
    return (
      <Flex justify="center" align="center">
        <Loader color="blue" />
      </Flex>
    );
  }

  const groupedSessions = sessions?.reduce((acc, session) => {
    const date = session.starting.split(" ")[0];
    if (!acc[date]) {
      acc[date] = Object.create(null);
    }
    const localizedLocationName = getI18nText(
      session.location.nameI18n,
      session.location.name,
      i18n
    );
    if (!acc[date][localizedLocationName]) {
      acc[date][localizedLocationName] = [];
    }
    acc[date][localizedLocationName].push(session);
    return acc;
  }, {});

  //Date filters
  const sortedDates = Object.keys(groupedSessions).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  return (
    <Stack gap="xl">
      {sortedDates.length === 0 ? (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack align="center" gap="md" py="xl">
            <IconFilter size={48} color="#a6b0bb" />
            <Title order={3} c="dimmed">
              {t("no_sessions_found")}
            </Title>
            <Text size="sm" c="dimmed" style={{ textAlign: "center" }}>
              {t("try_adjusting_filters")}
            </Text>
            {hasActiveFilters && (
              <Button
                variant="light"
                onClick={clearFilters}
                leftSection={<IconX size={16} />}
              >
                {t("clear_filters")}
              </Button>
            )}
          </Stack>
        </Paper>
      ) : (
        sortedDates.map((date) => (
          <Paper shadow="sm" p="xl" radius="md" key={date}>
            <Title order={2} mb="lg" style={{ color: "#2B3846" }}>
              {`${getTranslatedDay(date, t)}, ${getTranslatedMonth(date, t)} ${String(new Date(date).getDate()).padStart(2, "0")}, ${new Date(date).getFullYear()}`}
            </Title>
            {Object.keys(groupedSessions[date]).map((locationName) => (
              <Box key={locationName} mb="xl">
                <Flex align="center" gap="xs" mb="md">
                  <IconMapPin size={20} color="#2B3846" />
                  <Title order={3} style={{ color: "#2B3846" }}>
                    {locationName}
                  </Title>
                </Flex>
                <Timeline>
                  {groupedSessions[date][locationName].map((session) => {
                    const openable = isOpenable(session);

                    return (
                      <Timeline.Item
                        key={session.id}
                        bullet={<IconClock size={16} />}
                        title={
                          <Group gap="xs">
                            <Text fw={500}>
                              {new Date(session.starting).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                }
                              )}
                            </Text>
                            <Text c="dimmed">-</Text>
                            <Text c="dimmed">
                              {new Date(session.ending).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                }
                              )}
                            </Text>
                          </Group>
                        }
                      >
                        <Box
                          onClick={
                            openable
                              ? () => handleSessionClick(session)
                              : undefined
                          }
                          style={{
                            cursor: openable ? "pointer" : "default",
                            padding: "0.5rem",
                            marginLeft: "-0.5rem",
                            marginRight: "-0.5rem",
                            borderRadius: "8px",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (openable) {
                              e.currentTarget.style.backgroundColor = "#f8f9fa";
                            }
                          }}
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <Text fw={600} size="lg" mt="xs">
                            {getI18nText(
                              session.titleI18n,
                              session.title,
                              i18n
                            )}
                          </Text>
                          <Text
                            size="sm"
                            c="dimmed"
                            mt="xs"
                            lineClamp={openable ? 2 : undefined}
                          >
                            {getI18nText(
                              session.descriptionI18n,
                              session.description,
                              i18n
                            )}
                          </Text>
                          {session.speakers?.length > 0 && (
                            <Group gap="xs" mt="sm">
                              <IconUser size={16} />
                              <Text size="sm" fw={500}>
                                {session.speakers
                                  .map((s) => `${s.firstName} ${s.lastName}`)
                                  .join(", ")}
                              </Text>
                            </Group>
                          )}

                          {session?.tags?.length > 0 && (
                            <Group gap="xs" mt="md">
                              {session.tags?.map((tag) => (
                                <Badge
                                  key={tag.id}
                                  variant="light"
                                  color="#667C99"
                                  size="lg"
                                >
                                  {getI18nText(tag.titleI18n, tag.title, i18n)}
                                </Badge>
                              ))}
                            </Group>
                          )}
                        </Box>
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              </Box>
            ))}
          </Paper>
        ))
      )}
    </Stack>
  );
};

export default Agenda;
